import { apiRequest } from "./apiClient"
import { ALL_FACILITY_CODES } from "src/constants/facilities"

export interface ItemDto {
  code: string
  description: string
  unitOfMeasure?: string
  category?: string
  lastPrice?: number
  lastPriceDate?: string
  stockOnHand?: number
  facility?: string
  stockUnit?: string
  allergens?: string
  /** RMP last-updated timestamp (ISO-8601) + the user behind it. */
  updatedAt?: string
  updatedBy?: string
  /** RMP record version. */
  version?: number
}

interface GetItemsResponse {
  data?: ItemDto[]
  items?: ItemDto[]
}

/**
 * Fetches items from the Eclipse API.
 * Calls GET /items?facility=X&type=Y which hits RMP (CulinaryChefService)
 * for real item data at facilities with ALM ID mappings (KMD, KNY).
 *
 * No supplier parameter — this is a pure catalog view. Supplier-item-price
 * relationships will be a separate API (SIA) in the future.
 */
export const itemService = {
  /**
   * List ingredients (FOOD_INGREDIENT + NON_FOOD_INGREDIENT) at a facility.
   */
  listIngredients: async (facility: string): Promise<ItemDto[]> => {
    const response = await apiRequest<GetItemsResponse>(
      `/items?facility=${encodeURIComponent(facility)}&type=FOOD_INGREDIENT,NON_FOOD_INGREDIENT`,
    )
    return response.data ?? response.items ?? []
  },

  /**
   * List recipes (FINISHED_GOOD) at a facility.
   */
  listRecipes: async (facility: string): Promise<ItemDto[]> => {
    const response = await apiRequest<GetItemsResponse>(
      `/items?facility=${encodeURIComponent(facility)}&type=FINISHED_GOOD`,
    )
    return response.data ?? response.items ?? []
  },

  /**
   * Convert a quantity between an item's purchase and stock units (BR-UOM).
   * Optionally scoped to a supplier (uses the supplier-item case-pack coefficient).
   */
  convertUom: (
    facility: string,
    itemCode: string,
    fromUom: string,
    quantity: number,
    supplierCode?: string,
    toUom?: string,
  ): Promise<UomConversion> => {
    let url =
      `/uom/convert?facility=${encodeURIComponent(facility)}` +
      `&item=${encodeURIComponent(itemCode)}` +
      `&fromUom=${encodeURIComponent(fromUom)}` +
      `&quantity=${encodeURIComponent(quantity)}`
    if (supplierCode) url += `&supplier=${encodeURIComponent(supplierCode)}`
    if (toUom) url += `&toUom=${encodeURIComponent(toUom)}`
    return apiRequest<UomConversion>(url, { skipCache: true })
  },
}

export interface UomConversion {
  facility: string
  itemCode: string
  supplierCode?: string
  fromUom: string
  toUom: string
  stockUom: string
  purchaseUom?: string
  inputQuantity: number
  convertedQuantity: number
  coefficient: number
  coefficientSource: string
  decimals: number
  valid: boolean
  message?: string
}

const ALL_ITEM_TYPES = "FOOD_INGREDIENT,NON_FOOD_INGREDIENT,FINISHED_GOOD"

interface InventoryItemDto {
  itemCode: string
  itemDescription?: string
  unitOfMeasure?: string
  facilityCode?: string
  totalOnHand?: number
}

/**
 * Find a single item by code. Searches the active facility's full catalogue
 * first, then the other facilities, and finally falls back to the inventory
 * API (the item may exist in SageX3 but not yet in RMP). Returns null if not
 * found anywhere.
 */
export async function findItemByCode(facility: string, itemCode: string): Promise<ItemDto | null> {
  const inActive = (
    await apiRequest<GetItemsResponse>(
      `/items?facility=${encodeURIComponent(facility)}&type=${ALL_ITEM_TYPES}`,
    )
  ).data
  const found = (inActive ?? []).find((i) => i.code === itemCode)
  if (found) return found

  const others = ALL_FACILITY_CODES.filter((f) => f !== facility)
  const otherResults = await Promise.all(
    others.map((f) =>
      apiRequest<GetItemsResponse>(
        `/items?facility=${encodeURIComponent(f)}&type=${ALL_ITEM_TYPES}`,
      )
        .then((r) => (r.data ?? []).find((i) => i.code === itemCode) ?? null)
        .catch(() => null),
    ),
  )
  const foundElsewhere = otherResults.find((r) => r !== null)
  if (foundElsewhere) return foundElsewhere

  // Inventory fallback — item present in SageX3 but not RMP.
  const inv = await apiRequest<{ data?: InventoryItemDto[] }>(
    `/inventory?facility=${encodeURIComponent(facility)}`,
  ).catch(() => ({ data: [] as InventoryItemDto[] }))
  const invItem = (inv.data ?? []).find((i) => i.itemCode === itemCode)
  if (invItem) {
    return {
      code: invItem.itemCode,
      description: invItem.itemDescription || invItem.itemCode,
      unitOfMeasure: invItem.unitOfMeasure || undefined,
      category: "INVENTORY",
      facility: invItem.facilityCode || facility,
      stockOnHand: invItem.totalOnHand,
    }
  }
  return null
}
